import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { COMMON_TRANSLATE_KEYS } from '../constants/translate-keys';
import InputField from '../components/forms/form-elements/InputField';
import DescriptionInput from '../components/forms/form-elements/DescriptionInput';
import Button from '../components/buttons/Button';
import { useResponsiveWindow } from '../hooks/useResponsiveWindow';
import { contactUsSchema } from '../validation/schemas/contacts';
import { useAuth } from '../hooks/useAuth';
import { sendPartnerEmail } from '../api/contacts';
import { toast } from 'react-toastify';

interface IFormValues {
  name: string;
  surname: string;
  email: string;
  phone: string;
  message: string;
}

const defaultValues = {
  email: '',
  name: '',
  surname: '',
  message: '',
  phone: '',
};

const BecomePartner = () => {
  const { t } = useTranslation();

  const { isMobile } = useResponsiveWindow();

  const { user } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
    reset,
    setValue,
  } = useForm<IFormValues>({
    defaultValues,
    resolver: yupResolver(contactUsSchema),
    mode: 'onChange',
  });

  const hasSurname = useMemo(
    () => (user?.name.split(' ') || []).length > 1,
    [user]
  );

  const hasPhone = useMemo(() => !!user?.phone, [user]);

  const submitForm = useCallback(
    async (data: IFormValues) => {
      const { data: emailResult, error } = await sendPartnerEmail(data);

      if (error) {
        if (error.statusCode === 429) {
          toast.success(t(COMMON_TRANSLATE_KEYS.MESSAGES_RECEIVED), {
            theme: 'dark',
            className: 'text-center',
            bodyClassName: 'custom-toast-body',
          });
        } else {
          toast.error(error.error, {
            theme: 'dark',
            className: 'text-center',
            bodyClassName: 'custom-toast-body',
          });
        }
      }

      if (emailResult) {
        toast.success(t(COMMON_TRANSLATE_KEYS.MESSAGE_RECEIVED), {
          theme: 'dark',
          className: 'text-center',
          bodyClassName: 'custom-toast-body',
        });
      }

      const [firstName, lastName] = user?.name.split(' ') || [];

      reset(
        {
          ...defaultValues,
          email: user?.email || '',
          phone: user?.phone || '',
          name: firstName || '',
          surname: lastName || '',
        },
        {
          keepErrors: false,
          keepDirty: false,
          keepTouched: false,
          keepDefaultValues: true,
          keepValues: false,
        }
      );
    },
    [reset, t, user]
  );

  useEffect(() => {
    if (user) {
      const [firstName, lastName] = user.name.split(' ');

      setValue('name', firstName);
      setValue('surname', lastName);
      setValue('email', user.email);
      setValue('phone', user.phone);
    }
  }, [setValue, user]);

  return (
    <div className="flex flex-col px-8 items-center my-10 w-full h-full">
      <div className="flex flex-col gap-5 items-center w-full h-fit pb-10 overflow-x-clip">
        <div className="text-mainWhite text-xl pb-2 gap-3 flex flex-col w-full">
          <p className="font-bold">
            За да увеличите заетостта на вашия спортен комплекс, станете
            партньор на Bolarena, без да правите допълнителни разходи.
          </p>
          <p className="font-bold">Ние ще ви предложим:</p>
          <p className="text-start">
            - лесна и бърза резервация на вашите игрища
          </p>
          <p className="text-start">
            - максимална заетост на предлаганите от вас услуги в спортния
            комплекс
          </p>
          <p className="text-start">- видимост от n бр. членове</p>
          <p className="text-start">
            - административен панел и директна поддръжка от екипа на Bolarena
          </p>
          <p className="text-start">- статистика за:</p>
          <p className="text-start ps-3 text-functionalGreen font-bold">
            Вашите резервации
          </p>
          <p className="text-start ps-3 text-functionalGreen font-bold">
            Натоварени часове
          </p>
          <p className="text-start ps-3 text-functionalGreen font-bold">
            Отказани резервации
          </p>
          <p className="text-start ps-3 text-functionalGreen font-bold">
            Причини за отказ
          </p>
          <p className="text-start">
            - елиминиране на възможността от дублиране на часове
          </p>
          <p className="text-start">
            - намаляване на разходи, време за следене и запълване на свободните
            часове
          </p>
          <p className="text-start">- увеличаване на печалбите</p>
        </div>
        <h1 className="text-mainWhite text-xl font-bold text-center w-full md:w-[37rem] lg:w-[110%] px-8 py-3 lg:border-y-2 border-dividerGray">
          {t(COMMON_TRANSLATE_KEYS.CONTACT_US)}
        </h1>
        <form
          onSubmit={handleSubmit(submitForm)}
          className="md:w-[37rem] w-full flex flex-col gap-5"
        >
          <div className="flex flex-col gap-1 w-full">
            <p className="text-mainWhite">
              {t(COMMON_TRANSLATE_KEYS.NAME)}{' '}
              <span className="text-functionalRed">*</span>
            </p>
            <InputField
              error={t(errors.name?.message || '')}
              {...register('name')}
              placeholder={t(COMMON_TRANSLATE_KEYS.NAME)}
              disabled={true}
            />
          </div>
          <div className="flex flex-col gap-1 w-full">
            <p className="text-mainWhite">
              {t(COMMON_TRANSLATE_KEYS.SURNAME)}{' '}
              <span className="text-functionalRed">*</span>
            </p>
            <InputField
              error={t(errors.surname?.message || '')}
              {...register('surname')}
              placeholder={t(COMMON_TRANSLATE_KEYS.SURNAME)}
              disabled={hasSurname}
            />
          </div>
          <div className="flex flex-col gap-1 w-full">
            <p className="text-mainWhite">
              {t(COMMON_TRANSLATE_KEYS.EMAIL)}{' '}
              <span className="text-functionalRed">*</span>
            </p>
            <InputField
              error={t(errors.email?.message || '')}
              {...register('email')}
              placeholder={t(COMMON_TRANSLATE_KEYS.EMAIL)}
              disabled={true}
            />
          </div>
          <div className="flex flex-col gap-1 w-full">
            <p className="text-mainWhite">
              {t(COMMON_TRANSLATE_KEYS.PHONE)}{' '}
              <span className="text-functionalRed">*</span>
            </p>
            <InputField
              error={t(errors.phone?.message || '')}
              {...register('phone')}
              placeholder={t(COMMON_TRANSLATE_KEYS.PHONE)}
              disabled={hasPhone}
            />
          </div>
          <div className="flex flex-col gap-1 w-full">
            <p className="text-mainWhite">
              {t(COMMON_TRANSLATE_KEYS.MESSAGE)}{' '}
              <span className="text-functionalRed">*</span>
            </p>
            <DescriptionInput
              error={t(errors.message?.message || '')}
              {...register('message')}
              placeholder={t(COMMON_TRANSLATE_KEYS.MESSAGE)}
              height={10}
              width="100%"
            />
          </div>
          <div className="flex w-full justify-center">
            <Button
              backgroundColor="bg-functionalGreen"
              textColor="text-mainWhite"
              type="submit"
              width={isMobile ? undefined : 100}
              isDisabled={!isValid}
              isLoading={isSubmitting}
              loadingText={t(COMMON_TRANSLATE_KEYS.SENDING)}
            >
              {t(COMMON_TRANSLATE_KEYS.SEND)}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BecomePartner;
